/**
 * Secure Storage - Memory-based alternative to localStorage for sensitive data
 * Prevents XSS attacks by keeping sensitive data in memory only
 */

class SecureStorage {
    constructor() {
        this.data = new Map();
        this.encryptionKey = this.generateKey();
    }

    generateKey() {
        // Simple obfuscation key (not true encryption, but better than plain text)
        return Array.from({ length: 32 }, () => Math.random().toString(36).charAt(2)).join('');
    }

    obfuscate(data) {
        // Simple XOR obfuscation
        const str = JSON.stringify(data);
        let result = '';
        for (let i = 0; i < str.length; i++) {
            result += String.fromCharCode(
                str.charCodeAt(i) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
            );
        }
        return btoa(result); // Base64 encode
    }

    deobfuscate(obfuscatedData) {
        try {
            const str = atob(obfuscatedData); // Base64 decode
            let result = '';
            for (let i = 0; i < str.length; i++) {
                result += String.fromCharCode(
                    str.charCodeAt(i) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
                );
            }
            return JSON.parse(result);
        } catch (e) {
            console.error('Failed to deobfuscate data:', e);
            return null;
        }
    }

    setItem(key, value) {
        // Only store non-sensitive data in localStorage
        if (this.isSensitive(key)) {
            this.data.set(key, value);
            return true;
        }
        
        // For non-sensitive data, use localStorage normally
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (e) {
            console.error('localStorage setItem failed:', e);
            return false;
        }
    }

    getItem(key) {
        // Only store non-sensitive data in localStorage
        if (this.isSensitive(key)) {
            return this.data.get(key) || null;
        }
        
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (e) {
            console.error('localStorage getItem failed:', e);
            return null;
        }
    }

    removeItem(key) {
        if (this.isSensitive(key)) {
            this.data.delete(key);
            return true;
        }
        
        try {
            localStorage.removeItem(key);
            return true;
        } catch (e) {
            console.error('localStorage removeItem failed:', e);
            return false;
        }
    }

    clear() {
        this.data.clear();
        // Only clear non-sensitive items from localStorage
        const nonSensitiveKeys = ['coq_cart', 'coq_last_order_submit'];
        nonSensitiveKeys.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (e) {
                console.error('Failed to clear localStorage key:', key);
            }
        });
    }

    isSensitive(key) {
        const sensitiveKeys = [
            'coq_profile',
            'coq_customer',
            'supabase.auth.token',
            'sb-access-token',
            'sb-refresh-token'
        ];
        return sensitiveKeys.some(sensitive => key.includes(sensitive));
    }

    // Session persistence (survives page refresh but not browser close)
    persistToSession() {
        try {
            const sessionData = {};
            this.data.forEach((value, key) => {
                if (this.isSensitive(key)) {
                    sessionData[key] = this.obfuscate(value);
                }
            });
            sessionStorage.setItem('coq_secure_session', JSON.stringify(sessionData));
        } catch (e) {
            console.error('Failed to persist to session:', e);
        }
    }

    restoreFromSession() {
        try {
            const sessionData = sessionStorage.getItem('coq_secure_session');
            if (sessionData) {
                const parsed = JSON.parse(sessionData);
                Object.entries(parsed).forEach(([key, value]) => {
                    if (this.isSensitive(key)) {
                        this.data.set(key, this.deobfuscate(value));
                    }
                });
                sessionStorage.removeItem('coq_secure_session');
            }
        } catch (e) {
            console.error('Failed to restore from session:', e);
        }
    }
}

// Create singleton instance
const secureStorage = new SecureStorage();

// Restore from session on page load
secureStorage.restoreFromSession();

// Persist to session on page unload
window.addEventListener('beforeunload', () => {
    secureStorage.persistToSession();
});

export default secureStorage;
